/**
 * AppUpdatePrompt (T27.2.9) — the dismissible "Update in Play Store" nudge for a
 * native Android build with a newer versionCode on its track.
 *
 * Renders only after `checkForAppUpdate()` (apps/web/src/adapters/appUpdate.ts)
 * reports `updateAvailable` (either detector). On web and iOS the adapter
 * no-ops, so this nudge never appears there — the web/PWA freshness nudge (T29)
 * is a separate surface.
 *
 * One action — open Play. The prior cut's flexible in-app download + "restart to
 * finish" UI is dropped (its detection shared Google's slow cache anyway). Now
 * the single action is a predictable tap that deep-links to the Play Store page
 * via the `market://` scheme (the Play app intent), with the https Play URL as a
 * fallback when `market://` can't resolve. The install itself is Play's — we
 * open the page, never download/install the `.aab`.
 *
 * Per-versionCode dismissal. The native trigger re-runs on every resume, so a
 * dismissed nudge must not re-appear on every foreground (assistive nudge, not a
 * nag). We persist a "dismissed at versionCode V" flag in localStorage keyed on
 * the advertised versionCode; the nudge stays hidden while V is the latest, and
 * re-appears only when a NEWER versionCode is advertised.
 *
 * Bridge. The adapter is platform-only and DOM-free; `main.tsx` wires it to
 * dispatch `APP_UPDATE_AVAILABLE_EVENT` carrying the advertised versionCode +
 * name when `updateAvailable` fires. This component listens for that event and
 * shows the nudge unless that versionCode was already dismissed. Mirrors the
 * existing `SESSION_CLEARED_EVENT` window-event bridge, so the adapter stays
 * free of React.
 *
 * Styling is token-only (CP5) — every colour / spacing / radius resolves from a
 * `var(--token)`; no hex, no raw px. The bar reuses the Button primitive.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Button } from '../components';

/**
 * Window event the native update adapter raises when EITHER detector reports a
 * newer build. The detail carries the advertised versionCode (for the dismissal
 * key) and the optional advertised versionName (for display).
 */
export const APP_UPDATE_AVAILABLE_EVENT = 'shopista:app-update-available';

export interface AppUpdateAvailableDetail {
  /** Advertised versionCode — the dismissal key. May be null when only the
   *  secondary (Play API) detector fired (it surfaces no number). */
  versionCode: number | null;
  /** Advertised versionName from `mobile-latest.json`, when present. */
  versionName: string | null;
}

/** Play Store deep-link targets for the Shopista prod package. */
const PLAY_PACKAGE = 'app.shopista.ca';
export const PLAY_MARKET_URL = `market://details?id=${PLAY_PACKAGE}`;
export const PLAY_HTTPS_URL = `https://play.google.com/store/apps/details?id=${PLAY_PACKAGE}`;

/** localStorage key prefix for the per-versionCode dismissal flag. */
const DISMISS_KEY_PREFIX = 'shopista.appUpdateDismissed.';

/** Was the nudge for this versionCode already dismissed? */
function isDismissed(versionCode: number | null): boolean {
  if (versionCode === null) return false;
  try {
    return window.localStorage.getItem(`${DISMISS_KEY_PREFIX}${versionCode}`) === '1';
  } catch {
    // localStorage unavailable (private mode / disabled) — treat as not
    // dismissed; a nudge is the safe default over silently suppressing it.
    return false;
  }
}

/** Persist that the nudge for this versionCode was dismissed. */
function markDismissed(versionCode: number | null): void {
  if (versionCode === null) return;
  try {
    window.localStorage.setItem(`${DISMISS_KEY_PREFIX}${versionCode}`, '1');
  } catch {
    // Best-effort — if persistence fails the nudge may re-appear next resume,
    // which is acceptable (and better than crashing on dismiss).
  }
}

/**
 * Open the Play Store on the Shopista page. Prefer the `market://` app intent;
 * fall back to the https Play URL when the scheme can't resolve (no Play app).
 */
export function openPlayStore(): void {
  try {
    window.location.assign(PLAY_MARKET_URL);
  } catch {
    window.location.assign(PLAY_HTTPS_URL);
  }
}

const barStyle: CSSProperties = {
  position: 'fixed',
  // Sit above the bottom nav + safe-area inset so the nudge clears it.
  left: 'var(--space-3)',
  right: 'var(--space-3)',
  bottom: 'calc(var(--space-6) + env(safe-area-inset-bottom, 0px))',
  zIndex: 'var(--z-toast)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-4)',
  backgroundColor: 'var(--color-bg-raised)',
  color: 'var(--color-text-primary)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-md)',
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  maxWidth: '32rem',
  margin: '0 auto',
};

const messageStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

/**
 * Mount once near the top of the authenticated tree. Inert until the native
 * adapter raises `APP_UPDATE_AVAILABLE_EVENT` with a not-yet-dismissed
 * versionCode; then it shows the "Update in Play Store" bar.
 */
export function AppUpdatePrompt(): JSX.Element | null {
  const [versionCode, setVersionCode] = useState<number | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onAvailable = (event: Event): void => {
      const detail = (event as CustomEvent<AppUpdateAvailableDetail>).detail;
      const advertised = detail?.versionCode ?? null;
      // Suppress a nudge the user already dismissed for THIS versionCode; it
      // re-appears only when a newer versionCode is advertised.
      if (isDismissed(advertised)) return;
      setVersionCode(advertised);
      setShown(true);
    };
    window.addEventListener(APP_UPDATE_AVAILABLE_EVENT, onAvailable);
    return () => window.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, onAvailable);
  }, []);

  if (!shown) return null;

  const handleDismiss = (): void => {
    markDismissed(versionCode);
    setShown(false);
  };

  return (
    <div role="alert" aria-live="polite" data-testid="app-update-prompt" style={barStyle}>
      <span style={messageStyle}>A newer version of Shopista is available.</span>
      <Button variant="ghost" size="sm" onClick={handleDismiss}>
        Dismiss
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          openPlayStore();
        }}
      >
        Update
      </Button>
    </div>
  );
}
